import React, { useEffect, useMemo } from 'react'
import {
  Box,
  Chip,
  Checkbox,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  FormHelperText,
} from '@material-ui/core'
import { useController, useFormContext } from 'react-hook-form'
import useStyles from './styles'

const CheckedMultiSelect = ({
  name,
  label,
  options,
  defaultValue = [],
  valueKey = 'value',
  labelKey = 'label',
  ...props
}) => {
  const styles = useStyles()
  const { control, setValue } = useFormContext()

  const {
    field: { value = [], onChange },
    fieldState: { error } = {},
  } = useController({
    name,
    control,
    defaultValue: [],
  })

  useEffect(() => {
    const isValueEmptyOrMismatched =
      !Array.isArray(value) || value.length === 0 || (value.length > 0 && !value[0]?.[valueKey])

    if (defaultValue?.length && isValueEmptyOrMismatched) {
      const formatted = defaultValue.map(item => ({
        [valueKey]: item[valueKey],
        [labelKey]: item[labelKey],
      }))
      setValue(name, formatted, { shouldValidate: true })
    }
  }, [defaultValue, value, setValue, name, valueKey, labelKey])

  const getId = item => item?.[valueKey] ?? item
  const getLabel = item => item?.[labelKey] ?? item

  const selectedValues = useMemo(() => {
    if (!Array.isArray(value)) return []
    return value.map(v => String(getId(v)))
  }, [value, valueKey])

  const handleChange = event => {
    const selectedIds = event.target.value
    const selectedOptions = selectedIds.map(id => {
      const found = options.find(opt => String(getId(opt)) === String(id))
      return found
        ? { [valueKey]: getId(found), [labelKey]: getLabel(found) }
        : { [valueKey]: id, [labelKey]: id }
    })
    onChange(selectedOptions)
  }
  const handleDelete = itemToDelete => {
    const newValue = value.filter(v => getId(v) !== itemToDelete)
    onChange(newValue)
  }

  return (
    <FormControl fullWidth error={!!error} {...props}>
      <InputLabel shrink className={styles.labelInput}>
        {label}
      </InputLabel>
      <Select
        multiple
        label={label}
        value={selectedValues}
        onChange={handleChange}
        renderValue={() => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedValues.map(val => {
              const selectedOption = options.find(opt => String(getId(opt)) === String(val))
              if (!selectedOption) return null

              return (
                <Chip
                  key={val}
                  label={getLabel(selectedOption)}
                  onDelete={() => handleDelete(val)}
                  onMouseDown={e => e.stopPropagation()}
                  color="primary"
                />
              )
            })}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 224,
              width: 250,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
      >
        {options.map(option => (
          <MenuItem key={getId(option)} value={getId(option)}>
            <Checkbox checked={selectedValues.includes(getId(option))} color="primary" />
            {getLabel(option)}
          </MenuItem>
        ))}
      </Select>
      {error?.message && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  )
}

export default CheckedMultiSelect
